import { createSelector } from '@reduxjs/toolkit';

import FieldInformation from '@app/store/contract-setup/ContractSetupFieldInformation';
import FieldType from '@app/store/FieldType';
import { RootState } from '@app/store/types';

import { selectHostDataLabels, selectHosts } from './reducers/host';

export const setupSelector = (state: RootState) => state.contractSetup;
export const attachmentsSelector = (state: RootState) => state.contractSetup.attachments;
export const getAuthentications = (state: RootState) => setupSelector(state).authentications;
export const getMobileAuthentication = (state: RootState) => getAuthentications(state).mobile;
export const getPasswordAuthentication = (state: RootState) => getAuthentications(state).password;
/**
 * 파일첨부 요청 전체 수
 */
export const attachmentRequestTotalCountSelector = createSelector(
  attachmentsSelector,
  (attachments) => {
    return attachments.reduce((acc, attachment) => acc + attachment.requests.length, 0);
  }
);

export const getExitRoute = createSelector(setupSelector, (v) => v.exitRoute);
export const fieldsSelector = createSelector(setupSelector, (v) => v.fields);
export const getIsCheckSchedule = createSelector(setupSelector, (v) => v.schedule.isCheckSchedule);
export const informationSelector = createSelector(fieldsSelector, (v) => v.information);
export const fieldIdsSelector = createSelector(fieldsSelector, (v) => v.fieldIds);
export const getLatestFieldId = createSelector(fieldsSelector, (v) => v.latestFieldId);
export const getReminders = createSelector(setupSelector, (v) => v.reminder.reminders);
export const getScheduledDate = (state: RootState): string | null =>
  setupSelector(state).schedule.scheduledDate;
export const getLabelIds = createSelector(setupSelector, (v) => v.labelIds);
const getParticipantsSelector = (state: RootState) => setupSelector(state).participants;
export const getParticipantsError = (state: RootState) => getParticipantsSelector(state).error;
export const getParticipantsIds = (state: RootState) => getParticipantsSelector(state).ids;
export const getIsContractSetupSignInOrder = (state: RootState): boolean =>
  getParticipantsSelector(state).signingProgressType === 'inOrder';
export const participantFieldIdsSelector = createSelector(
  setupSelector,
  (v) => v.participants.fieldIds
);
export const positionsSelector = createSelector(fieldsSelector, (v) => v.positions);
export const sizesSelector = createSelector(fieldsSelector, (v) => v.sizes);
export const targetSelector = createSelector(fieldsSelector, (v) => v.target);

export const isFieldInserting = createSelector(setupSelector, (v) => v.isFieldInserting);

export const participantsSelector = createSelector(setupSelector, (v) => v.participants);
export const participantsByIdSelector = createSelector(participantsSelector, (v) => v.byId);
export const participantsIdsSelector = createSelector(participantsSelector, (v) => v.ids);

export const requiredVerificationSelector = createSelector(setupSelector, (v) => v.verifications);

export const getSizeById = (state: RootState, props: { fieldId: string }) =>
  sizesSelector(state)[props.fieldId];

/**
 * 템플릿을 통해 서명 요청할 때 모바일에서 필드 수정이 가능하지 않다는 안내를 확인했는지 반환한다.
 */
export const getConfirmedThatFieldCannotBeModified = (state: RootState) =>
  setupSelector(state).confirmedThatFieldCannotBeModified;

export const getPropsParticipantId = (_: RootState, props: { participantId: string }) =>
  props.participantId;

const getHost = (state: ReturnType<typeof setupSelector>) => state.host;

export const hostsByDataLabelSelector = createSelector(setupSelector, (v) =>
  selectHosts(getHost(v))
);
export const hostDataLabelsSelector = createSelector(setupSelector, (v) =>
  selectHostDataLabels(getHost(v))
);
export const defaultHostLabelSelector = createSelector(
  setupSelector,
  (v) => getHost(v).defaultHostDataLabel
);

export const defaultHostSelector = createSelector(
  [hostsByDataLabelSelector, defaultHostLabelSelector],
  (hosts, defaultHostDataLabel) => (defaultHostDataLabel ? hosts[defaultHostDataLabel] : null)
);

export const isDefaultHostInitializedSelector = createSelector(
  defaultHostLabelSelector,
  (label) => !!label
);

export interface ParticipantAuthAnswers {
  digitalCertificate: {
    bizNumber?: string;
    companyName?: string;
  };
  mobile: {
    phoneNumber?: string;
    realName?: string;
  };
  password: {
    hint?: string;
    value?: string;
  };
}

export const getParticipantOrder = createSelector(
  [participantsIdsSelector, getPropsParticipantId],
  (participantsIds: string[], participantId) => {
    return participantsIds.indexOf(participantId) + 1;
  }
);

/**
 * 특정 서명자의 정보를 가져온다.
 */
export const getParticipant = createSelector(
  participantsByIdSelector,
  getPropsParticipantId,
  (byId, participantId) => byId[participantId]
);

/**
 * 특정 서명자의 이름을 가져온다.
 */
export const getParticipantName = createSelector(getParticipant, (participant) => participant.name);

/**
 * 특정 서명자의 서명 참여 수단(EMAIL|PHONE)을 가져온다.
 */
export const getParticipantMeansType = createSelector(
  getParticipant,
  (participant) => participant.meansType
);

interface Field {
  [id: string]: FieldInformation & {
    page: number;
    relativeX: number;
    relativeY: number;
  };
}

/**
 * 모든 필드에 대한 정보를 반환한다.
 */
export const getFields = createSelector(
  fieldIdsSelector,
  informationSelector,
  positionsSelector,
  (fieldIds, information, positions) =>
    fieldIds.reduce<Field>((state, id) => {
      state[id] = { ...information[id], ...positions[id] };
      return state;
    }, {} as Field)
);

/**
 * 페이지에 존재하는 필드의 아이디 리스트를 반환한다.
 */

export const getFieldIdsInPageMap = createSelector(positionsSelector, (positions) => {
  const map: Record<string, string[]> = {};

  Object.keys(positions).forEach((id) => {
    const { page } = positions[id];

    if (!map[page]) {
      map[page] = [id];
    } else {
      map[page].push(id);
    }
  });

  return map;
});

export const getTrimmedFieldDataLabelMap = createSelector(
  fieldIdsSelector,
  informationSelector,
  (ids, informations) => {
    const dataLabelMap: Record<string, string> = {};

    ids.forEach((id) => {
      dataLabelMap[id] = informations[id].dataLabel.trim();
    });

    return dataLabelMap;
  }
);

/**
 * 서명자 필드 dataLabel이 유효한지 값이 있는지 확인합니다.
 */
export const getIsParticipantFieldDataLabelValid = createSelector(
  fieldIdsSelector,
  informationSelector,
  (ids, informations) => {
    const dataLabelsSet = new Set();
    return ids.every((id) => {
      const trimmedDataLabel = informations[id].dataLabel.trim();
      if (!trimmedDataLabel || dataLabelsSet.has(trimmedDataLabel)) {
        return false;
      }

      dataLabelsSet.add(trimmedDataLabel);
      return true;
    });
  }
);

/**
 * 선택한 필드 유형 중 가장 최근에 추가된 필드 아이디를 반환한다.
 */
export const getLatestFieldData = createSelector(
  getLatestFieldId,
  targetSelector,
  sizesSelector,
  informationSelector,
  (latestFieldIDByType, target, sizes, information) => {
    if (!target) {
      return null;
    }

    const { type } = target;

    if (type === FieldType.SIGNATURE_FIELD) return null;

    let latestFieldID = null;
    if (type === FieldType.CHECKBOX_GROUP) {
      latestFieldID = latestFieldIDByType.checkboxGroup;
    } else if (type === FieldType.TEXT_FIELD) {
      latestFieldID = latestFieldIDByType.textField;
    }

    if (!latestFieldID) {
      return null;
    }

    const informationValue = information[latestFieldID];
    const sizeValue = sizes[latestFieldID];

    return { ...informationValue, ...sizeValue };
  }
);

/**
 * 입력중인 필드의 허용 서명 유형(AllowSignatureTypes)을 반환한다.
 */
export const getTargetAllowSignatureType = createSelector(targetSelector, (target) => {
  if (!target) {
    return undefined;
  }

  if (target.type === FieldType.SIGNATURE_FIELD) {
    const { allowSignatureTypes } = target;
    return allowSignatureTypes;
  }

  return undefined;
});

/**
 * 입력중인 필드의 유형을 반환한다.
 */
export const getTargetType = createSelector(targetSelector, (target) => {
  if (!target) {
    return null;
  }

  const { type } = target;

  return type;
});

/**
 * 각 서명자에 지정된 첨부파일을 서명자 정보와 함께 반환한다.
 */
export const getParticipantAttachments = (state: RootState) => {
  const participantsById = participantsByIdSelector(state);
  const attachments = attachmentsSelector(state);

  return attachments.map(({ participantId, requests }) => ({
    participantId,
    participantName: participantsById[participantId]?.name,
    requests,
  }));
};

/**
 * 각 서명자에게 설정된 첨부파일이 있는지 반환한다.
 */
export const getHasParticipantAttachments = (state: RootState): boolean =>
  getParticipantAttachments(state).flatMap((attachment) => attachment.requests).length !== 0;
